import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import {
  type TrackWorkspaceShellEvent,
  type WorkspaceShellButtonAction,
  type WorkspaceShellScreenName,
} from '@heidi/workspace/contracts';

const SCREEN_NAMES: Record<WorkspaceShellScreenName, ScreenName> = {
  workspace_selector: ScreenName.WorkspaceSelector,
  workspace_top_menu: ScreenName.WorkspaceTopMenu,
};

const BUTTON_ACTIONS: Record<WorkspaceShellButtonAction, ButtonAction> = {
  workspace_selector_workspace_selected: ButtonAction.WorkspaceSelectorWorkspaceSelected,
  workspace_selector_join_team: ButtonAction.WorkspaceSelectorJoinTeam,
  workspace_top_menu_workspace_selected: ButtonAction.WorkspaceTopMenuWorkspaceSelected,
  workspace_top_menu_switch_blocked_recording:
    ButtonAction.WorkspaceTopMenuSwitchBlockedRecording,
  workspace_top_menu_join_team: ButtonAction.WorkspaceTopMenuJoinTeam,
};

/**
 * The workspace shell's analytics port, bound to `track()`. Every emit is a
 * newer dispatch site, so each opts out of Amplitude (Snowplow-first policy),
 * exactly as the shell's surfaces did before they moved behind the door.
 */
export const trackWorkspaceShellEvent: TrackWorkspaceShellEvent = (event) => {
  switch (event.event_name) {
    case 'screen_viewed':
      track({
        event_name: TrackingEventName.ScreenViewed,
        props: {
          screen_name: SCREEN_NAMES[event.props.screen_name],
          entry_point: event.props.entry_point,
        },
        options: { amplitude: false },
      });
      return;
    case 'button_clicked':
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          screen_name: SCREEN_NAMES[event.props.screen_name],
          action: BUTTON_ACTIONS[event.props.action],
          label: event.props.label,
        },
        options: { amplitude: false },
      });
      return;
    default: {
      const exhaustive: never = event;
      throw new Error(`Unhandled workspace shell event: ${JSON.stringify(exhaustive)}`);
    }
  }
};
